// src/state/editStore.js
// Stores Edit contents (rich pins) per editId in localStorage.
//
// v1 (legacy): productIds only
//   key:  "veya_edit_products_v1::${editId}" -> [1,2,3]
//
// v2 (current): rich edit items
//   key:  "veya_edit_items_v2::${editId}" -> [{ id, kind, productId?, imageUrl?, note?, title?, createdAt, ... }, ...]
//
// Meta (name, templateSlug, timestamps) lives in editsStore.js.
import { ensureImagePinFeatures } from "./ensurePinFeatures";
import { getEditMeta } from "./editsStore";
import { emitEditChanged } from "./editEvents";
import { saveEditItems, saveEditMeta } from "../lib/editApi";
import { getShopperToken } from "../lib/api";

const KEY_V1 = (editId) => `veya_edit_products_v1::${editId}`;
const KEY_V2 = (editId) => `veya_edit_items_v2::${editId}`;

function safeJsonParse(raw, fallback) {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function safeReadV1(editId) {
  if (!editId) return [];
  const raw = localStorage.getItem(KEY_V1(editId));
  const arr = safeJsonParse(raw, []);
  return Array.isArray(arr) ? arr : [];
}

function safeReadV2(editId) {
  if (!editId) return [];
  const raw = localStorage.getItem(KEY_V2(editId));
  const arr = safeJsonParse(raw, []);
  return Array.isArray(arr) ? arr : [];
}

function safeWriteV2(editId, arr) {
  if (!editId) return;
  try {
    localStorage.setItem(KEY_V2(editId), JSON.stringify(arr));
  } catch {}
}

function uid(prefix = "pin") {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

async function persistEditToBackend(editId, items) {
  if (!editId || !getShopperToken()) return;

  const meta = getEditMeta(editId);

  await saveEditMeta({
    id: editId,
    name: (meta?.name || "").trim() || "Untitled edit",
    templateSlug: meta?.templateSlug || null,
  });

  await saveEditItems(editId, items);
}

function normaliseKind(k) {
  const v = String(k || "").toLowerCase();
  if (v === "pinned_product" || v === "product") return "pinned_product";
  if (v === "image" || v === "screenshot" || v === "photo") return "image";
  if (v === "note" || v === "text") return "note";
  return v || "image";
}

function normaliseItem(input) {
  if (!input) return null;

  // If they passed a number -> pinned product
  if (typeof input === "number") {
    return {
      id: uid("pin"),
      kind: "pinned_product",
      productId: input,
      createdAt: Date.now(),
    };
  }

  // If they passed {productId:...} or a pin object
  if (typeof input === "object") {
    const kind = normaliseKind(input.kind || input.type);
    const createdAt = typeof input.createdAt === "number" ? input.createdAt : Date.now();

    const out = {
      ...input,
      id: input.id || uid("pin"),
      kind,
      createdAt,
    };

    // Normalise common fields
    if (out.image && !out.imageUrl) out.imageUrl = out.image;
    if (out.src && !out.imageUrl) out.imageUrl = out.src;
    if (out.url && !out.imageUrl) out.imageUrl = out.url;

    return out;
  }

  return null;
}

// --------- Migration (v1 -> v2) ---------

function ensureMigrated(editId) {
  const v2 = safeReadV2(editId);
  if (v2.length) return v2;

  const v1 = safeReadV1(editId);
  if (!v1.length) return [];

  const migrated = v1
    .filter((x) => x !== null && x !== undefined)
    .map((productId) => ({
      id: uid("pin"),
      kind: "pinned_product",
      productId,
      createdAt: Date.now(),
    }));

  safeWriteV2(editId, migrated);
  return migrated;
}

// ---- Public API ----

/**
 * Returns rich edit items:
 * [{id, kind, productId?, imageUrl?, note?, title?, createdAt, ...}]
 */
export function getEditItems(editId) {
  return ensureMigrated(editId);
}

/**
 * Overwrite all items (used when saving from EditPanel or applying templates).
 */
export function setEditItems(items, editId) {
  if (!editId) return;
  const arr = Array.isArray(items) ? items : [];
  const norm = arr.map(normaliseItem).filter(Boolean);

  safeWriteV2(editId, norm);
  emitEditChanged(editId);

  if (getShopperToken()) {
    persistEditToBackend(editId, norm).catch((e) => {
      console.warn("persistEditToBackend failed", e);
    });
  }
}

/**
 * True if a given productId is present in this edit (any pinned_product item).
 */
export function isInEdit(productId, editId) {
  if (!editId) return false;
  const items = ensureMigrated(editId);
  return items.some((it) => it.kind === "pinned_product" && it.productId === productId);
}

/**
 * Add either:
 *  - a productId number (creates a pinned_product item)
 *  - a full pin item { kind, productId?, imageUrl?, note?, title?, ... }
 *
 * De-dupe rule:
 *  - pinned_product: only one per productId
 *  - others: always add (notes/images can repeat)
 */
export function addToEdit(pinOrProductId, editId) {
  if (!editId) return;
  const items = ensureMigrated(editId);
  const item = normaliseItem(pinOrProductId);
  if (!item) return;

  if (item.kind === "pinned_product" && item.productId != null) {
    if (items.some((x) => x.kind === "pinned_product" && x.productId === item.productId)) return;
  }

 const next = [item, ...items];
safeWriteV2(editId, next);
emitEditChanged(editId);

if (getShopperToken()) {
  persistEditToBackend(editId, next).catch((e) => {
    console.warn("persistEditToBackend failed", e);
  });
}

}

/**
 * Remove product pin (pinned_product) by productId.
 * Does NOT remove screenshots/notes.
 */
export function removeFromEdit(productId, editId) {
  if (!editId) return;
  const items = ensureMigrated(editId);
  const next = items.filter((it) => !(it.kind === "pinned_product" && it.productId === productId));
  safeWriteV2(editId, next);
  emitEditChanged(editId);

  if (getShopperToken()) {
    persistEditToBackend(editId, next).catch((e) => {
      console.warn("persistEditToBackend failed", e);
    });
  }
}

/**
 * Remove a specific item by its id.
 */
export function removeEditItem(itemId, editId) {
  if (!editId) return;
  const items = ensureMigrated(editId);
  const next = items.filter((it) => it.id !== itemId);
  safeWriteV2(editId, next);
  emitEditChanged(editId);

   if (getShopperToken()) {
    persistEditToBackend(editId, next).catch((e) => {
      console.warn("persistEditToBackend failed", e);
    });
  }
}

export function clearEdit(editId) {
  if (!editId) return;
  safeWriteV2(editId, []);
  emitEditChanged(editId);

   if (getShopperToken()) {
    persistEditToBackend(editId, []).catch((e) => {
      console.warn("persistEditToBackend failed", e);
    });
  }
}

/**
 * Accepts either:
 *  - [1,2,3] (productIds)
 *  - [{productId: 1}, {productId: 2}] (pins)
 *  - full rich pins from templates/moods
 */
export function setEditPins(pinsOrIds, editId) {
  if (!editId) return;

  const arr = Array.isArray(pinsOrIds) ? pinsOrIds : [];
  const norm = arr.map(normaliseItem).filter(Boolean);

  safeWriteV2(editId, norm);
  emitEditChanged(editId);
}

export function deleteEditItems(editId) {
  if (!editId) return;
  try {
    localStorage.removeItem(KEY_V2(editId));
    localStorage.removeItem(KEY_V1(editId));
  } catch {}
  emitEditChanged(editId);
}